import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, TextField, Checkbox, CircularProgress } from '@mui/material';

// Helper to parse API response
const getStandardResults = (response) => response.data?.data || [];

// Simple debounce function
export const debounce = (func, wait) => {
    let timeout;
    const debounced = (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timeout);
    return debounced;
};

// Helper to get ID consistently (handles both _id and id)
const getId = (item) => item?._id || item?.id;

const MultiSelectSearchAutocomplete = ({
    label,
    searchFunction,
    value = [],
    onChange,
    initialOptions = [],
    getOptionLabel,
    disabled = false,
    minWidth = 200,
    allItems = [],
}) => {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    //   NEW: Cache for items that were selected from search results
    // This persists items that aren't in allItems/initialOptions
    const [selectedItemsCache, setSelectedItemsCache] = useState([]);

    // Store refs to avoid dependency issues
    const allItemsRef = useRef(allItems);
    const initialOptionsRef = useRef(initialOptions);
    const valueRef = useRef(value);
    const selectedItemsCacheRef = useRef(selectedItemsCache);

    // Update refs when props change
    useEffect(() => {
        allItemsRef.current = allItems;
    }, [allItems]);

    useEffect(() => {
        initialOptionsRef.current = initialOptions;
    }, [initialOptions]);

    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    useEffect(() => {
        selectedItemsCacheRef.current = selectedItemsCache;
    }, [selectedItemsCache]);

    //   Helper function to merge options without duplicates
    const mergeOptions = useCallback((baseOptions, additionalOptions) => {
        const merged = [...baseOptions];
        const existingIds = new Set(baseOptions.map(item => getId(item)));

        additionalOptions.forEach(opt => {
            if (opt && !existingIds.has(getId(opt))) {
                merged.push(opt);
                existingIds.add(getId(opt));
            }
        });

        return merged;
    }, []);

    //   Helper to get all known items (allItems + initialOptions + cache)
    const getAllKnownItems = useCallback(() => {
        return mergeOptions(
            mergeOptions(allItemsRef.current, initialOptionsRef.current),
            selectedItemsCacheRef.current
        );
    }, [mergeOptions]);

    // Initialize options when data arrives
    useEffect(() => {
        if (initialOptions.length === 0 && allItems.length === 0) {
            return;
        }

        const knownItems = getAllKnownItems();
        const selectedItems = knownItems.filter(item => value.includes(getId(item)));
        const merged = mergeOptions(selectedItems, initialOptions);

        setOptions(merged);
    }, [initialOptions, allItems, value, getAllKnownItems, mergeOptions]);

    // Memoized search function
    const performSearch = useCallback(async (searchTerm) => {
        if (!searchFunction || !searchTerm) return;

        setLoading(true);
        try {
            const response = await searchFunction(searchTerm, { page: 1, limit: 20 });
            const results = getStandardResults(response);

            // Get currently selected items from all known sources
            const knownItems = getAllKnownItems();
            const selectedItems = knownItems.filter(
                item => valueRef.current.includes(getId(item))
            );

            // Merge: selected items first, then search results
            const mergedOptions = mergeOptions(selectedItems, results);

            setOptions(mergedOptions);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setLoading(false);
        }
    }, [searchFunction, getAllKnownItems, mergeOptions]);

    // Create stable debounced function
    const debouncedSearchRef = useRef(null);

    useEffect(() => {
        debouncedSearchRef.current = debounce(performSearch, 300);
        return () => {
            debouncedSearchRef.current?.cancel();
        };
    }, [performSearch]);

    // Handle input change
    const handleInputChange = useCallback((event, newInputValue, reason) => {
        if (reason === 'input') {
            setInputValue(newInputValue);

            if (newInputValue.length >= 1) {
                debouncedSearchRef.current?.(newInputValue);
            } else {
                // Reset to initial options + selected items
                const knownItems = getAllKnownItems();
                const selectedItems = knownItems.filter(
                    item => valueRef.current.includes(getId(item))
                );
                const merged = mergeOptions(selectedItems, initialOptionsRef.current);
                setOptions(merged);
            }
        } else if (reason === 'clear') {
            setInputValue('');
        }
        // Ignore 'reset' reason to prevent clearing on selection
    }, [getAllKnownItems, mergeOptions]);

    //   FIXED: Handle selection change - cache newly selected items
    const handleChange = useCallback((event, newValue) => {
        // Update the cache with any items that aren't in allItems or initialOptions
        const newSelectedItems = newValue.filter(item => {
            const itemId = getId(item);
            const inAllItems = allItemsRef.current.some(i => getId(i) === itemId);
            const inInitialOptions = initialOptionsRef.current.some(i => getId(i) === itemId);
            return !inAllItems && !inInitialOptions;
        });

        if (newSelectedItems.length > 0) {
            setSelectedItemsCache(prev => {
                const merged = [...prev];
                newSelectedItems.forEach(item => {
                    if (!merged.some(i => getId(i) === getId(item))) {
                        merged.push(item);
                    }
                });
                return merged;
            });
        }

        // Call parent onChange with IDs
        onChange(newValue.map(v => getId(v)));
    }, [onChange]);

    //   FIXED: Resolve selected values - look in allItems, initialOptions, cache, AND current options
    const selectedObjects = useMemo(() => {
        const result = [];
        const addedIds = new Set();

        // Helper to add items without duplicates
        const addFromSource = (source) => {
            for (const item of source) {
                const itemId = getId(item);
                if (value.includes(itemId) && !addedIds.has(itemId)) {
                    result.push(item);
                    addedIds.add(itemId);
                }
            }
        };

        // Check all sources for selected items
        addFromSource(allItems);
        addFromSource(initialOptions);
        addFromSource(selectedItemsCache);
        addFromSource(options);

        return result;
    }, [allItems, initialOptions, selectedItemsCache, options, value]);

    // Handle open
    const handleOpen = useCallback(() => {
        setIsOpen(true);

        // Get all known items including cache
        const knownItems = getAllKnownItems();
        const selectedItems = knownItems.filter(item => value.includes(getId(item)));

        // Also include items from current options that are selected
        const additionalSelected = options.filter(item =>
            value.includes(getId(item)) && !selectedItems.some(s => getId(s) === getId(item))
        );

        const allSelected = [...selectedItems, ...additionalSelected];
        const merged = mergeOptions(allSelected, initialOptions);

        setOptions(merged);
    }, [value, initialOptions, options, getAllKnownItems, mergeOptions]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setInputValue('');
    }, []);

    return (
        <Autocomplete
            multiple
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            options={options}
            loading={loading}
            getOptionLabel={getOptionLabel}
            value={selectedObjects}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            disabled={disabled}
            isOptionEqualToValue={(option, val) => getId(option) === getId(val)}
            filterOptions={(x) => x} // Disable built-in filtering - we use server-side search
            disableCloseOnSelect
            clearOnBlur={false}
            blurOnSelect={false}
            renderOption={(props, option, { selected }) => {
                const { key, ...restProps } = props;
                return (
                    <li key={getId(option)} {...restProps}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {getOptionLabel(option)}
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    sx={{ minWidth }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default MultiSelectSearchAutocomplete;